/**
 * The two rules a screen would otherwise invent (Sprint 084.0, ADR-0091;
 * DEC-8). Here so that no component decides them (Principle 3).
 */
import { solarDay } from './events.js';
import { sunPosition } from './position.js';
import { instantToLocal, minutesOf, offsetAt } from './time.js';
const QUARTER_HOUR = 15;
const DAY_MINUTES = 1440;
/**
 * The first moment a sun is shown at: today, at the current local time rounded
 * to the quarter hour — or today's solar noon if the sun is down then, so that
 * turning the sun on never shows a black scene first.
 *
 * `now` is an argument: this package reads no clock, and a caller that owns one
 * passes it.
 */
export function initialMoment(now, zone, latitude, longitude) {
    const { localDate, localTime } = instantToLocal(now, zone);
    const rounded = Math.min(Math.round(minutesOf(localTime) / QUARTER_HOUR) * QUARTER_HOUR, DAY_MINUTES - QUARTER_HOUR);
    const candidate = `${pad(Math.floor(rounded / 60))}:${pad(rounded % 60)}`;
    if (sunPosition(now, latitude, longitude).aboveHorizon) {
        return { localDate, localTime: candidate };
    }
    const day = solarDay(localDate, zone, latitude, longitude, { path: false });
    return { localDate, localTime: day.solarNoon.localTime };
}
/**
 * The shape of a day on a 24-hour strip. Fractions are of the local clock's
 * day, so a sunrise at 06:00 sits at a quarter even on the day clocks change.
 */
export function dayTimeline(day) {
    const at = (localTime) => minutesOf(localTime) / DAY_MINUTES;
    const noon = at(day.solarNoon.localTime);
    const sunrise = day.sunrise.kind === 'at' ? at(day.sunrise.localTime) : undefined;
    const sunset = day.sunset.kind === 'at' ? at(day.sunset.localTime) : undefined;
    let nightBands;
    if (day.sunrise.kind === 'none') {
        nightBands = day.sunrise.reason === 'polar-night' ? [[0, 1]] : [];
    }
    else {
        nightBands = [];
        if (sunrise !== undefined && sunrise > 0) {
            nightBands.push([0, sunrise]);
        }
        if (sunset !== undefined && sunset < 1) {
            nightBands.push([sunset, 1]);
        }
    }
    // The site's day (Sprint 085.7c): the stretches the sun is up but behind the
    // terrain are the day's up-time less the visible intervals. An interval that
    // runs to midnight ends at "00:00", which is the next day's start: 1.
    const site = day.site;
    const siteSunrise = site?.sunrise === undefined ? undefined : at(site.sunrise.localTime);
    const siteSunset = site?.sunset === undefined ? undefined : at(site.sunset.localTime);
    const hiddenBands = [];
    if (site !== undefined) {
        const up = day.sunrise.kind === 'none'
            ? day.sunrise.reason === 'polar-day'
                ? [0, 1]
                : [0, 0]
            : [sunrise ?? 0, sunset ?? 1];
        const visible = site.visible
            .map(({ start, end }) => {
            const from = at(start.localTime);
            const to = at(end.localTime);
            return [from, to <= from ? 1 : to];
        })
            .sort((a, b) => a[0] - b[0]);
        let cursor = up[0];
        for (const [from, to] of visible) {
            if (from > cursor)
                hiddenBands.push([cursor, Math.min(from, up[1])]);
            cursor = Math.max(cursor, to);
        }
        if (cursor < up[1])
            hiddenBands.push([cursor, up[1]]);
    }
    return {
        ...(sunrise === undefined ? {} : { sunrise }),
        solarNoon: noon,
        ...(sunset === undefined ? {} : { sunset }),
        nightBands,
        snaps: [sunrise, siteSunrise, noon, siteSunset, sunset]
            .filter((value) => value !== undefined)
            .sort((a, b) => a - b),
        ...(siteSunrise === undefined ? {} : { siteSunrise }),
        ...(siteSunset === undefined ? {} : { siteSunset }),
        hiddenBands: hiddenBands.filter(([from, to]) => to > from)
    };
}
function pad(value) {
    return String(value).padStart(2, '0');
}
/** The eight compass points, clockwise from north (Sprint 084.3). */
export const COMPASS_POINTS = [
    'north',
    'north-east',
    'east',
    'south-east',
    'south',
    'south-west',
    'west',
    'north-west'
];
/**
 * The compass word for an azimuth from true north: the nearest of eight, each
 * 45° wide. A screen's own four-point helpers answer a different question —
 * which way is up — and are not this.
 */
export function compassPoint(azimuthDegrees) {
    const normalised = ((azimuthDegrees % 360) + 360) % 360;
    return COMPASS_POINTS[Math.round(normalised / 45) % 8] ?? 'north';
}
/**
 * The season a solstice or equinox is **for this site** (Sprint 084.3,
 * ADR-0091 Rule 7). The keys are month-named because June is winter in Sydney;
 * this is where a label for a hemisphere comes from, so no component decides
 * it. The equator's (latitude 0) is read as northern, as a convention.
 */
export function seasonOf(key, latitude) {
    const northern = latitude >= 0;
    switch (key) {
        case 'march-equinox':
            return northern ? 'spring' : 'autumn';
        case 'june-solstice':
            return northern ? 'summer' : 'winter';
        case 'september-equinox':
            return northern ? 'autumn' : 'spring';
        case 'december-solstice':
            return northern ? 'winter' : 'summer';
    }
}
/**
 * The offset in force at an instant, and whether it is **summer time**: an
 * offset above the smaller of the zone's January and July offsets that year
 * (Sprint 084.3). A fixed offset is never summer time. No zone rule is written
 * here — `Intl`'s data supplies the offsets.
 */
export function zoneReading(zone, instant) {
    const offset = offsetAt(zone, instant);
    if (zone.kind === 'offset') {
        return { utcOffsetMinutes: offset, summerTime: false };
    }
    const year = new Date(instant).getUTCFullYear();
    const standard = Math.min(offsetAt(zone, Date.UTC(year, 0, 15)), offsetAt(zone, Date.UTC(year, 6, 15)));
    return { utcOffsetMinutes: offset, summerTime: offset > standard };
}
/**
 * The local time at a fraction of the day's strip, snapped to an event within
 * `snapMinutes` (Sprint 084.3, DEC-3). Clamped to 00:00–23:59, so a drag past
 * either end reads as the end, never as the next day.
 */
export function timeAtFraction(fraction, snaps = [], snapMinutes = 10) {
    const clamped = Math.min(Math.max(fraction, 0), 1);
    let minutes = Math.round(clamped * DAY_MINUTES);
    for (const snap of snaps) {
        const snapAt = Math.round(snap * DAY_MINUTES);
        if (Math.abs(snapAt - minutes) <= snapMinutes) {
            minutes = snapAt;
            break;
        }
    }
    minutes = Math.min(minutes, DAY_MINUTES - 1);
    return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
}
/** Where a local time sits on the day's strip, 0 at midnight. */
export function fractionOfTime(localTime) {
    return minutesOf(localTime) / DAY_MINUTES;
}
/**
 * The time `minutes` after `localTime`, wrapping at midnight — what _Play the
 * day_ advances by (Sprint 084.3).
 */
export function advanceTime(localTime, minutes) {
    const next = (((minutesOf(localTime) + Math.round(minutes)) % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;
    return `${pad(Math.floor(next / 60))}:${pad(next % 60)}`;
}
//# sourceMappingURL=presentation.js.map